/** UXP-00004 — Product Intelligence Workspace framework (mirrors pkg/productintel) */

export const UXP_PI_ID = 'UXP-00004';

export interface ProductSpec {
  id: string;
  asin: string;
  sku: string;
  title: string;
  brand: string;
  health: number;
  revenue: number;
  image_url?: string;
  status: string;
}

export interface HealthScoreSpec {
  dimension: string;
  label: string;
  score: number;
  trend?: 'up' | 'down' | 'stable';
}

export interface KGEdge {
  from: string;
  to: string;
  kind: string;
}

export interface AIRecommendation {
  type: string;
  title: string;
  impact: string;
  confidence: number;
}

export const EXPLORER_VIEWS = [
  'Table', 'Cards', 'Gallery', 'Kanban', 'Comparison',
  'Collections', 'Favorites', 'Pinned', 'Recently Viewed',
];

export const PRODUCT_DNA_DIMENSIONS = [
  'Product DNA', 'Visual DNA', 'Content DNA', 'Commercial DNA',
  'Customer DNA', 'Competitive DNA', 'Brand Alignment', 'Confidence Score',
];

export const KNOWLEDGE_GRAPH_NODES = [
  'Variants', 'Bundles', 'Accessories', 'Categories', 'Brands',
  'Campaigns', 'Competitors', 'Collections', 'Marketplace Relationships',
];

export const HEALTH_DIMENSIONS = [
  'Overall', 'Content', 'Creative', 'SEO', 'Advertising',
  'Inventory', 'Pricing', 'Compliance', 'Customer', 'Marketplace',
];

export const ADVISOR_CAPABILITIES = [
  'Product Summary', 'Growth Opportunities', 'Risk Analysis',
  'Optimization Recommendations', 'Publishing Advice', 'Competitive Analysis', 'Forecasts',
];

export const PERFORMANCE_METRICS = [
  'Revenue', 'Profit', 'Traffic', 'Conversion', 'Advertising',
  'Reviews', 'Inventory', 'Pricing', 'Forecasts',
];

export const AI_RECOMMENDATION_TYPES = [
  'Images', 'Listings', 'A+', 'Video', 'Pricing',
  'Advertising', 'Inventory', 'Promotions', 'Bundles',
];

export const WORKSPACE_REGIONS = [
  'Explorer', 'Overview', 'Performance', 'Assets', 'Timeline',
  'Knowledge Graph', 'Recommendations', 'AI Workspace', 'Publishing', 'Audit',
];

export const BULK_OPERATIONS = [
  'Bulk AI', 'Bulk Publishing', 'Bulk Approvals',
  'Bulk Optimization', 'Bulk Comparison', 'Bulk Export',
];

export const PUBLISHING_STAGES = [
  'Draft', 'Review', 'Approval', 'Release Packages',
  'Publishing', 'Rollback', 'Marketplace Selection',
];

export const COLLABORATION_FEATURES = [
  'Comments', 'Mentions', 'Assignments',
  'Internal Notes', 'Review Queue', 'Decision History',
];

export const SEARCH_CAPABILITIES = [
  'Semantic Search', 'AI Search', 'Natural Language Search',
  'Saved Searches', 'Smart Filters', 'Collections',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support',
  'Reduced Motion', 'High Contrast',
];

export const ARCHITECTURE_STAGES = [
  'Product Explorer', 'Product DNA Engine', 'Knowledge Graph',
  'Product Health', 'Performance Analytics', 'AI Advisor',
  'AI Recommendations', 'Assets & Publishing', 'Bulk Operations', 'Collaboration',
];

export const ANALYTICS_METRICS = [
  'Workspace Usage', 'Product Health Trends', 'AI Adoption',
  'Recommendation Acceptance', 'Publishing Success', 'Business Impact',
];

export const LIVE_ROUTES: Record<string, string> = {
  catalog: '/amazon/catalog',
  catalogOps: '/amazon/catalog-ops',
  listingOptimizer: '/ai/listing',
};

export const DEMO_PRODUCTS: ProductSpec[] = [
  { id: 'p1', asin: 'B0CX1234AB', sku: 'SKU-001', title: 'Premium Wireless Earbuds Pro', brand: 'SoundWave', health: 92, revenue: 48200, status: 'active' },
  { id: 'p2', asin: 'B0CX5678CD', sku: 'SKU-002', title: 'Ultra Slim Laptop Stand', brand: 'DeskPro', health: 78, revenue: 23100, status: 'active' },
  { id: 'p3', asin: 'B0CX9012EF', sku: 'SKU-003', title: 'Organic Cotton Bedsheet Set', brand: 'SleepWell', health: 85, revenue: 31500, status: 'active' },
  { id: 'p4', asin: 'B0CX3456GH', sku: 'SKU-004', title: 'Smart Home Hub Gen 3', brand: 'ConnectIQ', health: 67, revenue: 18900, status: 'review' },
  { id: 'p5', asin: 'B0CX7890IJ', sku: 'SKU-005', title: 'Stainless Steel Water Bottle', brand: 'HydroLife', health: 94, revenue: 55800, status: 'active' },
  { id: 'p6', asin: 'B0CX2345KL', sku: 'SKU-006', title: 'Yoga Mat Premium 6mm', brand: 'FlexFit', health: 71, revenue: 12400, status: 'draft' },
];

export const DEMO_HEALTH_SCORES: HealthScoreSpec[] = [
  { dimension: 'overall', label: 'Overall', score: 84, trend: 'up' },
  { dimension: 'content', label: 'Content', score: 88, trend: 'up' },
  { dimension: 'creative', label: 'Creative', score: 76, trend: 'stable' },
  { dimension: 'seo', label: 'SEO', score: 72, trend: 'down' },
  { dimension: 'advertising', label: 'Advertising', score: 81, trend: 'up' },
  { dimension: 'inventory', label: 'Inventory', score: 90, trend: 'stable' },
  { dimension: 'pricing', label: 'Pricing', score: 79, trend: 'up' },
  { dimension: 'compliance', label: 'Compliance', score: 95, trend: 'stable' },
  { dimension: 'customer', label: 'Customer', score: 86, trend: 'up' },
  { dimension: 'marketplace', label: 'Marketplace', score: 83, trend: 'stable' },
];

export const DEMO_KG_EDGES: KGEdge[] = [
  { from: 'Premium Wireless Earbuds Pro', to: 'Earbuds Case', kind: 'accessory' },
  { from: 'Premium Wireless Earbuds Pro', to: 'SoundWave', kind: 'brand' },
  { from: 'Premium Wireless Earbuds Pro', to: 'Electronics > Audio', kind: 'category' },
  { from: 'Premium Wireless Earbuds Pro', to: 'SP Campaign Q3', kind: 'campaign' },
  { from: 'Premium Wireless Earbuds Pro', to: 'Competitor X Pro', kind: 'competitor' },
  { from: 'Premium Wireless Earbuds Pro', to: 'Amazon.in', kind: 'marketplace' },
];

export const DEMO_ADVISOR_INSIGHTS = [
  'Add 2 backend keywords to improve organic rank — estimated +12% sessions',
  'Main image CTR 23% below category average — test lifestyle hero shot',
  'Bundle with Earbuds Case for +18% AOV based on purchase patterns',
  'Price elasticity suggests ₹50 increase with minimal conversion impact',
  'Compliance: missing energy rating attribute for Amazon.in listing',
];

export const DEMO_RECOMMENDATIONS: AIRecommendation[] = [
  { type: 'Listings', title: 'Optimize title for top 3 search terms', impact: '+15% sessions', confidence: 87 },
  { type: 'Images', title: 'Generate lifestyle image set (4 variants)', impact: '+8% CTR', confidence: 82 },
  { type: 'A+', title: 'A+ module: comparison chart vs competitors', impact: '+5% conversion', confidence: 79 },
  { type: 'Pricing', title: 'Dynamic price adjustment for peak season', impact: '+₹12K/mo profit', confidence: 74 },
  { type: 'Advertising', title: 'Increase exact-match bid on top converter', impact: '+22% ROAS', confidence: 91 },
];

export const DEMO_PERFORMANCE = [
  { label: 'Revenue', value: '₹48.2K', change: '+12%', positive: true },
  { label: 'Profit', value: '₹14.6K', change: '+8%', positive: true },
  { label: 'Traffic', value: '3,842', change: '+15%', positive: true },
  { label: 'Conversion', value: '4.2%', change: '-0.3%', positive: false },
  { label: 'Ad Spend', value: '₹6.1K', change: '+5%', positive: true },
  { label: 'Reviews', value: '4.6★', change: '+0.1', positive: true },
];

export function buildProductIntelDashboard() {
  return {
    explorer_views: EXPLORER_VIEWS.length,
    product_dna_dimensions: PRODUCT_DNA_DIMENSIONS.length,
    knowledge_graph_nodes: KNOWLEDGE_GRAPH_NODES.length,
    health_dimensions: HEALTH_DIMENSIONS.length,
    advisor_capabilities: ADVISOR_CAPABILITIES.length,
    performance_metrics: PERFORMANCE_METRICS.length,
    ai_recommendation_types: AI_RECOMMENDATION_TYPES.length,
    workspace_regions: WORKSPACE_REGIONS.length,
  };
}
